// src/features/fiscalization/ProvidersTab.tsx
// Fiscal providers (the intermediary that submits receipts to the tax
// authority on the tenant's behalf) — connect, configure, test.
// Covers: ListFiscalProviders, CreateFiscalProvider, GetFiscalProvider (via
// list item), UpdateFiscalProvider, DeleteFiscalProvider, TestFiscalProvider.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useFiscalizationFiscalProvider, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';
import { Select } from '@/components/Select';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import type { FiscalProvider } from '@core/types/fiscalization';
import { FiscalProviderTypeValues } from '@core/types/fiscalization';
import { errMessage, humanizeEnum } from './format';
import { mapFiscalProviderStatusToVariant } from './mapStatusToVariant';

const TYPE_OPTIONS = FiscalProviderTypeValues.filter((v) => v !== 'FISCAL_PROVIDER_TYPE_UNSPECIFIED');

interface ProviderForm {
  name: string;
  description: string;
  type: string;
  country_code: string;
  endpoint_url: string;
  protocol_version: string;
  credential_id: string;
}

const EMPTY: ProviderForm = { name: '', description: '', type: TYPE_OPTIONS[0], country_code: '', endpoint_url: '', protocol_version: '', credential_id: '' };

export const ProvidersTab = observer(() => {
  const store = useFiscalizationFiscalProvider();
  const { t } = useI18n();
  const [editing, setEditing] = useState<FiscalProvider | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<ProviderForm>(EMPTY);
  const [deleteTarget, setDeleteTarget] = useState<FiscalProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; success: boolean; message: string } | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  useEffect(() => {
    store.ListFiscalProviders({ pagination: { cursor: '', limit: 50, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setFormOpen(true); };
  const openEdit = (p: FiscalProvider) => {
    setEditing(p);
    setForm({
      name: p.name ?? '', description: p.description ?? '', type: p.type ?? TYPE_OPTIONS[0],
      country_code: p.country_code ?? '', endpoint_url: p.endpoint_url ?? '', protocol_version: p.protocol_version ?? '', credential_id: p.credential_id ?? '',
    });
    setFormOpen(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        entity: editing ? (editing.entity as any) : ({} as any),
        name: form.name, description: form.description, type: form.type as any,
        country_code: form.country_code, endpoint_url: form.endpoint_url, protocol_version: form.protocol_version,
        credential_id: form.credential_id, metadata: {},
      };
      if (editing?.entity?.id) {
        await store.UpdateFiscalProvider(editing.entity.id, payload as any);
      } else {
        await store.CreateFiscalProvider(payload as any);
      }
      setFormOpen(false);
    } catch (err) {
      setError(errMessage(err, t('fiscalization.providers.saveError')));
    }
  };

  const handleTest = async (p: FiscalProvider) => {
    if (!p.entity?.id) return;
    setTestingId(p.entity.id);
    try {
      const res = await store.TestFiscalProvider(p.entity.id, {} as any);
      setTestResult({ id: p.entity.id, success: res.success, message: res.success ? t('fiscalization.providers.testSuccess') : (res.error_message || t('fiscalization.providers.testFailed')) });
    } catch {
      setTestResult({ id: p.entity.id, success: false, message: t('fiscalization.providers.testFailed') });
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.entity?.id) return;
    await store.DeleteFiscalProvider(deleteTarget.entity.id);
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('fiscalization.providers.subtitle')}</p>
        <Button size="sm" onClick={openCreate}>{t('fiscalization.providers.create')}</Button>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Table striped hoverable>
        <TableHeader>
          <TableRow>
            <TableHead>{t('common.name')}</TableHead>
            <TableHead>{t('fiscalization.providers.type')}</TableHead>
            <TableHead>{t('fiscalization.providers.country')}</TableHead>
            <TableHead>{t('common.status')}</TableHead>
            <TableHead className="text-right">{t('common.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {store.isLoading ? (
            <TableRow><TableCell colSpan={5} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
          ) : store.items.length === 0 ? (
            <TableEmpty colSpan={5} message={t('fiscalization.providers.empty')} />
          ) : (
            store.items.map((p) => (
              <TableRow key={p.entity?.id}>
                <TableCell>
                  {p.name}
                  {testResult && testResult.id === p.entity?.id && (
                    <div className={`text-xs mt-1 ${testResult.success ? 'text-success' : 'text-destructive'}`}>{testResult.message}</div>
                  )}
                </TableCell>
                <TableCell>{humanizeEnum(p.type, 'FISCAL_PROVIDER_TYPE_')}</TableCell>
                <TableCell>{p.country_code || '--'}</TableCell>
                <TableCell><Badge variant={mapFiscalProviderStatusToVariant(p.status)} size="sm">{humanizeEnum(p.status, 'FISCAL_PROVIDER_STATUS_')}</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" isLoading={testingId === p.entity?.id} onClick={() => handleTest(p)}>{t('fiscalization.providers.test')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>{t('common.edit')}</Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(p)}>{t('common.remove')}</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-8">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => setFormOpen(false)} aria-hidden="true" />
          <form onSubmit={submit} role="dialog" aria-modal="true" className="relative mx-4 w-full max-w-lg rounded-lg border border-border bg-background p-5 shadow-xl space-y-4">
            <h3 className="text-base font-semibold text-foreground">{editing ? t('fiscalization.providers.edit') : t('fiscalization.providers.create')}</h3>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Input label={t('common.name')} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} required />
            <Input label={t('common.description')} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            <div className="grid grid-cols-2 gap-3">
              <Select label={t('fiscalization.providers.type')} value={form.type} onChange={(e) => setForm((p) => ({ ...p, type: e.target.value }))}>
                {TYPE_OPTIONS.map((v) => (
                  <option key={v} value={v}>{humanizeEnum(v, 'FISCAL_PROVIDER_TYPE_')}</option>
                ))}
              </Select>
              <Input label={t('fiscalization.providers.country')} value={form.country_code} onChange={(e) => setForm((p) => ({ ...p, country_code: e.target.value }))} placeholder="DE" />
            </div>
            <Input label={t('fiscalization.providers.endpointUrl')} value={form.endpoint_url} onChange={(e) => setForm((p) => ({ ...p, endpoint_url: e.target.value }))} placeholder="https://api.example.com" />
            <div className="grid grid-cols-2 gap-3">
              <Input label={t('fiscalization.providers.protocolVersion')} value={form.protocol_version} onChange={(e) => setForm((p) => ({ ...p, protocol_version: e.target.value }))} />
              <Input label={t('fiscalization.providers.credentialId')} value={form.credential_id} onChange={(e) => setForm((p) => ({ ...p, credential_id: e.target.value }))} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setFormOpen(false)} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted">{t('common.cancel')}</button>
              <Button type="submit" size="sm" isLoading={store.isSaving}>{editing ? t('common.saveChanges') : t('common.create')}</Button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('fiscalization.providers.deleteConfirmTitle')}
        destructive
        isConfirming={store.isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
});

export default ProvidersTab;
